/**
 * Monthly payables calendar — generated, never stored.
 *
 * Two payment runs a month, anchored on the 6th and the 20th. A payable day
 * never lands on a Monday, a Friday, a weekend or a holiday, so there is always
 * room for the approval step: the anchor snaps to the nearest Tuesday/Wednesday/
 * Thursday, preferring the later date when both directions are equally close.
 * Submission is then 2 business days before payment and approval 1 business day
 * before, counting around weekends and statutory holidays.
 *
 * This reproduces the published Aug–Dec 2026 schedule exactly, so every future
 * month follows the same shape without anyone maintaining a list.
 */

const PAYMENT_ANCHORS = [6, 20];
// Tue = 2, Wed = 3, Thu = 4.
const ALLOWED_PAYMENT_DOW = [2, 3, 4];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const CALENDAR_RULE_NOTE =
  "Submission dates are 2 business days prior to payment date, with approvals done 1 " +
  "business day prior to payment. Any payable day that landed on a Monday or holiday was " +
  "pushed to the following business day to allow for approval process.";

/* ── date helpers (all UTC so a day never shifts across timezones) ────────── */

const utc = (y, m, d) => new Date(Date.UTC(y, m, d));
const addDays = (date, n) => new Date(date.getTime() + n * 86400000);
const ymd = (date) => date.toISOString().slice(0, 10);
const isWeekend = (date) => date.getUTCDay() === 0 || date.getUTCDay() === 6;

/** Anonymous Gregorian computus — used only to locate Good Friday. */
const easterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return utc(year, month - 1, day);
};

/** Nth given weekday of a month, e.g. nthDow(2026, 8, 1, 1) = 1st Monday of Sep. */
const nthDow = (year, monthIndex, dow, n) => {
  const first = utc(year, monthIndex, 1);
  const shift = (dow - first.getUTCDay() + 7) % 7;
  return utc(year, monthIndex, 1 + shift + (n - 1) * 7);
};

/**
 * Monday strictly before a given day of the month. Victoria Day is the Monday
 * *preceding* May 25, so when the 25th is itself a Monday it falls a week earlier.
 */
const mondayStrictlyBefore = (year, monthIndex, day) => {
  let d = addDays(utc(year, monthIndex, day), -1);
  while (d.getUTCDay() !== 1) d = addDays(d, -1);
  return d;
};

const holidayCache = new Map();

/**
 * Alberta statutory + federal holidays. Fixed-date holidays that fall on a
 * weekend are also observed on the following Monday, which is what actually
 * closes the office.
 */
export const holidaysFor = (year) => {
  if (holidayCache.has(year)) return holidayCache.get(year);

  const fixed = [
    utc(year, 0, 1), // New Year's Day
    utc(year, 6, 1), // Canada Day
    utc(year, 8, 30), // National Day for Truth and Reconciliation
    utc(year, 10, 11), // Remembrance Day
    utc(year, 11, 25), // Christmas Day
    utc(year, 11, 26), // Boxing Day
  ];

  const observed = [];
  fixed.forEach((d) => {
    if (d.getUTCDay() === 6) observed.push(addDays(d, 2));
    if (d.getUTCDay() === 0) observed.push(addDays(d, 1));
  });

  const set = new Set(
    [
      ...fixed,
      ...observed,
      nthDow(year, 1, 1, 3), // Family Day — 3rd Monday of February
      addDays(easterSunday(year), -2), // Good Friday
      mondayStrictlyBefore(year, 4, 25), // Victoria Day
      nthDow(year, 7, 1, 1), // Heritage Day — 1st Monday of August
      nthDow(year, 8, 1, 1), // Labour Day — 1st Monday of September
      nthDow(year, 9, 1, 2), // Thanksgiving — 2nd Monday of October
    ].map(ymd)
  );

  holidayCache.set(year, set);
  return set;
};

export const isHoliday = (date) => holidaysFor(date.getUTCFullYear()).has(ymd(date));

const isBusinessDay = (date) => !isWeekend(date) && !isHoliday(date);

/** A day the money can actually move: Tue–Thu, not a holiday. */
const isPayableDay = (date) =>
  ALLOWED_PAYMENT_DOW.includes(date.getUTCDay()) && !isHoliday(date);

/**
 * Snap an anchor to the nearest payable day. Scans outward one day at a time,
 * checking the later date first so an exact tie resolves forward.
 */
const snapToPayableDay = (anchor) => {
  for (let offset = 0; offset <= 10; offset += 1) {
    const forward = addDays(anchor, offset);
    if (isPayableDay(forward)) return forward;
    if (offset === 0) continue;
    const backward = addDays(anchor, -offset);
    if (isPayableDay(backward)) return backward;
  }
  return anchor;
};

/** Step back `count` business days from `date`. */
const businessDaysBefore = (date, count) => {
  let remaining = count;
  let cursor = date;
  while (remaining > 0) {
    cursor = addDays(cursor, -1);
    if (isBusinessDay(cursor)) remaining -= 1;
  }
  return cursor;
};

/** The two cycles of one month, in payment-date order. */
export const cyclesForMonth = (year, monthIndex) =>
  PAYMENT_ANCHORS.map((anchorDay) => {
    const paymentDate = snapToPayableDay(utc(year, monthIndex, anchorDay));
    return {
      month: MONTH_NAMES[paymentDate.getUTCMonth()],
      year: paymentDate.getUTCFullYear(),
      submitBy: ymd(businessDaysBefore(paymentDate, 2)),
      approveBy: ymd(businessDaysBefore(paymentDate, 1)),
      paymentDate: ymd(paymentDate),
    };
  }).sort((a, b) => a.paymentDate.localeCompare(b.paymentDate));

/**
 * The viewer's own calendar day as a UTC-midnight date. Everything else in this
 * module works in UTC, so converting `new Date()` with `toISOString()` would read
 * as tomorrow through a Mountain-Time evening and hide a cycle paying today.
 */
export const localToday = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
};

/**
 * Rolling calendar starting at the month containing `from`.
 * `months` controls how far ahead it runs.
 */
export const buildPayablesCalendar = ({ from = localToday(), months = 12 } = {}) => {
  const startYear = from.getUTCFullYear();
  const startMonth = from.getUTCMonth();
  const out = [];
  for (let i = 0; i < months; i += 1) {
    const cursor = utc(startYear, startMonth + i, 1);
    out.push(...cyclesForMonth(cursor.getUTCFullYear(), cursor.getUTCMonth()));
  }
  return out;
};

/**
 * Cycles still to come, cut off by date rather than by month — a run earlier in
 * the current month drops away while a later one in that same month stays. A
 * cycle counts as upcoming right up to and including its payment date.
 */
export const upcomingCycles = ({ today = localToday(), months = 12, limit } = {}) => {
  const todayStr = ymd(today);
  const rows = buildPayablesCalendar({ from: today, months }).filter(
    (c) => c.paymentDate >= todayStr
  );
  return typeof limit === "number" ? rows.slice(0, limit) : rows;
};

/** The cycle an invoice submitted today would fall into. */
export const currentCycle = ({ today = localToday() } = {}) => {
  const todayStr = ymd(today);
  return (
    buildPayablesCalendar({ from: today, months: 3 }).find((c) => c.submitBy >= todayStr) || null
  );
};

/** "Aug 4, 2026" — matches the published calendar's formatting. */
export const formatCycleDate = (value) => {
  if (!value) return "—";
  const [y, m, d] = String(value).split("-").map(Number);
  if (!y || !m || !d) return "—";
  return utc(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};
